import React, { useState, useEffect, useRef } from 'react';
import { CreateSeriesRequest } from '../../types/series';
import { ScoreType, ScoreRuleAudience } from '../../types/activity';
import { seriesAPI } from '../../services/seriesAPI';
import { academicPublicAPI } from '../../services/academicPublicAPI';
import { departmentAPI as adminDepartmentAPI, userAPI } from '../../services/adminAPI';
import { departmentAPI as publicDepartmentAPI } from '../../services/api';
import { SeriesPresetPreviewResponse, SeriesPresetDefinition, SeriesPresetCode, SeriesPresetConfig } from '../../types/presets';
import PresetConfigPanel from '../presets/PresetConfigPanel';
import MultiSelectField from '../presets/MultiSelectField';
import OrganizerSelector from '../events/OrganizerSelector';
import { Department } from '../../types/admin';
import { validateSeriesPresetConfig } from '../../utils/presetValidation';
import { assertManagerOrganizers } from '../../utils/seriesHelpers';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../types/auth';
import { toast } from 'react-toastify';
import { Plus, Trash } from '@phosphor-icons/react';

// Helper functions for preset initialization
const buildEnabledRules = (preset: SeriesPresetDefinition): Record<string, boolean> => {
    const rules: Record<string, boolean> = {};
    for (const rule of preset.supportedRules) {
        rules[rule.ruleKey] = rule.required ? true : rule.enabledByDefault;
    }
    return rules;
};

const buildInitialConfig = (preset: SeriesPresetDefinition): Record<string, unknown> => {
    const config: Record<string, unknown> = {};
    for (const rule of preset.supportedRules) {
        for (const field of rule.fieldDefinitions) {
            if (field.defaultValue !== undefined && field.defaultValue !== null) {
                config[field.fieldName] = field.defaultValue;
            }
        }
    }
    return config;
};

interface SeriesFormProps {
    onSubmit: (data: CreateSeriesRequest) => void;
    loading?: boolean;
    initialData?: Partial<CreateSeriesRequest>;
    title?: string;
    onCancel?: () => void;
    /** When true, omit outer page chrome (used inside CreateSeries hero layout). */
    embedded?: boolean;
    /** Khoa trong phạm vi quản lý (Manager) — soft scope check */
    managerDepartmentIds?: number[];
}

const SeriesForm: React.FC<SeriesFormProps> = ({
    onSubmit,
    loading = false,
    initialData = {},
    title = 'Tạo chuỗi sự kiện mới',
    onCancel,
    embedded = false,
    managerDepartmentIds: managerDepartmentIdsProp
}) => {
    const { userRole, username } = useAuth();
    const [managerDepartmentIds, setManagerDepartmentIds] = useState<number[]>(
        managerDepartmentIdsProp ?? []
    );

    const [formData, setFormData] = useState<CreateSeriesRequest>(() => {
        const defaultOrganizerIds =
            initialData.organizerIds?.length
                ? initialData.organizerIds
                : (managerDepartmentIdsProp?.length === 1 ? managerDepartmentIdsProp : []);

        const defaultData: CreateSeriesRequest = {
            name: '',
            description: '',
            milestonePoints: {},
            scoreType: ScoreType.REN_LUYEN,
            registrationStartDate: '',
            registrationDeadline: '',
            requiresApproval: true,
            ticketQuantity: undefined,
            minimumRequirementEnabled: false,
            minimumRequiredEvents: undefined,
            minimumPenaltyPoints: undefined,
            targetSemesterId: undefined,
            audience: ScoreRuleAudience.ALL_PARTICIPANTS,
            departmentIds: [],
            organizerIds: defaultOrganizerIds,
            isImportant: false,
            mandatoryForFacultyStudents: false,
            isDraft: true,
            presetCode: (initialData.presetCode ?? '') as SeriesPresetCode,
            presetConfig: initialData.presetConfig ?? undefined
        };

        return {
            ...defaultData,
            ...initialData,
            organizerIds: initialData.organizerIds ?? defaultOrganizerIds,
        };
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [milestoneEntries, setMilestoneEntries] = useState<Array<{ count: number; points: number }>>([]);
    const [milestoneInput, setMilestoneInput] = useState({ count: '', points: '' });
    const [unlimitedTickets, setUnlimitedTickets] = useState(!formData.ticketQuantity);
    const [presets, setPresets] = useState<SeriesPresetDefinition[]>([]);
    const [selectedPresetCode, setSelectedPresetCode] = useState<string>(
        initialData.presetCode && initialData.presetCode !== 'CUSTOM' ? initialData.presetCode : ''
    );
    const [enabledRules, setEnabledRules] = useState<Record<string, boolean>>({});
    const [presetPreview, setPresetPreview] = useState<SeriesPresetPreviewResponse | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [semesters, setSemesters] = useState<Array<{ id: number; name: string }>>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const isEditing = !!(initialData && Object.keys(initialData).length > 0);
    const hasInitializedPreset = useRef(false);

    // Load presets
    useEffect(() => {
        const fetchPresets = async () => {
            const res = await seriesAPI.getSeriesPresets();
            if (res.status && res.data) {
                setPresets(res.data as SeriesPresetDefinition[]);
            }
        };
        fetchPresets();
    }, []);

    // Initialize preset state from initialData when editing
    useEffect(() => {
        if (hasInitializedPreset.current || presets.length === 0 || !isEditing) return;

        if (initialData.presetCode && initialData.presetCode !== 'CUSTOM') {
            const preset = presets.find(p => p.code === initialData.presetCode);
            if (preset) {
                setSelectedPresetCode(initialData.presetCode);
                setEnabledRules(buildEnabledRules(preset));
                const initialConfig = initialData.presetConfig
                    ? { ...buildInitialConfig(preset), ...(initialData.presetConfig as Record<string, unknown>) }
                    : buildInitialConfig(preset);
                setFormData(prev => {
                    const updates: Partial<CreateSeriesRequest> = {
                        presetCode: initialData.presetCode as SeriesPresetCode,
                        presetConfig: initialConfig as SeriesPresetConfig
                    };
                    if (initialConfig.primaryScoreType !== undefined) {
                        updates.scoreType = initialConfig.primaryScoreType as ScoreType;
                    }
                    if (initialConfig.milestonePoints !== undefined && typeof initialConfig.milestonePoints === 'object') {
                        updates.milestonePoints = initialConfig.milestonePoints as Record<number, number>;
                    }
                    if (initialConfig.minimumRequirementEnabled !== undefined) {
                        updates.minimumRequirementEnabled = !!initialConfig.minimumRequirementEnabled;
                    }
                    if (initialConfig.minimumRequiredEvents !== undefined) {
                        updates.minimumRequiredEvents = initialConfig.minimumRequiredEvents as number;
                    }
                    if (initialConfig.minimumPenaltyPoints !== undefined) {
                        updates.minimumPenaltyPoints = initialConfig.minimumPenaltyPoints as number;
                    }
                    return { ...prev, ...updates };
                });
            } else {
                // Preset not found, fall back to custom mode
                setSelectedPresetCode('');
                setFormData(prev => ({
                    ...prev,
                    presetCode: 'CUSTOM' as SeriesPresetCode,
                    presetConfig: undefined
                }));
            }
        } else if (initialData.presetCode === 'CUSTOM' || !initialData.presetCode) {
            setSelectedPresetCode('');
            setFormData(prev => ({
                ...prev,
                presetCode: 'CUSTOM' as SeriesPresetCode,
                presetConfig: undefined
            }));
        }
        hasInitializedPreset.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presets, isEditing]);

    // Load semesters
    useEffect(() => {
        const fetchSemesters = async () => {
            try {
                const res = await academicPublicAPI.getSemesters();
                if (res && Array.isArray(res)) {
                    setSemesters(res.map((s: any) => ({ id: s.id, name: s.name })));
                }
            } catch (error) {
                console.error('Lỗi khi tải danh sách học kỳ:', error);
            }
        };
        fetchSemesters();
    }, []);

    // Load departments — ưu tiên public API (manager cũng gọi được), fallback admin
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const publicRes = await publicDepartmentAPI.getAll();
                let list: Department[] = [];
                if (publicRes.status && Array.isArray(publicRes.data) && publicRes.data.length > 0) {
                    list = publicRes.data;
                } else {
                    const adminRes = await adminDepartmentAPI.getDepartments();
                    if (adminRes.status && adminRes.data) {
                        list = adminRes.data;
                    }
                }
                setDepartments(list);
            } catch (error) {
                console.error('Lỗi khi tải danh sách khoa:', error);
            }
        };
        fetchDepartments();
    }, []);

    // Soft-load manager department scope khi prop chưa truyền
    useEffect(() => {
        if (managerDepartmentIdsProp?.length) {
            setManagerDepartmentIds(managerDepartmentIdsProp);
            return;
        }
        if (userRole !== Role.MANAGER || !username) return;

        const loadManagerScope = async () => {
            try {
                const res = await userAPI.getUsers('MANAGER');
                if (!res.status || !res.data) return;
                const me = res.data.find(
                    (u) => u.username?.toLowerCase() === username.toLowerCase()
                );
                if (me?.departmentIds?.length) {
                    setManagerDepartmentIds(me.departmentIds);
                    setFormData((prev) => {
                        if (prev.organizerIds?.length) return prev;
                        if (me.departmentIds!.length === 1) {
                            return { ...prev, organizerIds: [...me.departmentIds!] };
                        }
                        return prev;
                    });
                }
            } catch (error) {
                console.error('Lỗi khi tải phạm vi khoa của manager:', error);
            }
        };
        loadManagerScope();
    }, [managerDepartmentIdsProp, userRole, username]);

    const externalOptions = {
        departmentIds: departments.map(d => ({ value: d.id, label: d.name })),
    };

    // Sync milestoneEntries from formData.milestonePoints
    useEffect(() => {
        if (formData.milestonePoints) {
            setMilestoneEntries(
                Object.entries(formData.milestonePoints)
                    .map(([count, points]) => ({ count: parseInt(count), points: points as number }))
                    .sort((a, b) => a.count - b.count)
            );
        } else {
            setMilestoneEntries([]);
        }
    }, [formData.milestonePoints]);

    const handlePresetCodeChange = (code: string) => {
        setSelectedPresetCode(code);
        if (!code) {
            // Custom mode
            setEnabledRules({});
            setPresetPreview(null);
            setFormData(prev => ({
                ...prev,
                presetCode: 'CUSTOM' as SeriesPresetCode,
                presetConfig: undefined
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                presetCode: code as SeriesPresetCode
            }));
        }
    };

    const handlePresetConfigChange = (config: Record<string, unknown>) => {
        setFormData(prev => {
            const updates: Partial<CreateSeriesRequest> = {
                presetConfig: config as SeriesPresetConfig
            };
            // Sync derived fields from config so existing UI still works as fallback
            if (config.primaryScoreType !== undefined) {
                updates.scoreType = config.primaryScoreType as ScoreType;
            }
            if (config.milestonePoints !== undefined && typeof config.milestonePoints === 'object') {
                updates.milestonePoints = config.milestonePoints as Record<number, number>;
            }
            if (config.minimumRequirementEnabled !== undefined) {
                updates.minimumRequirementEnabled = !!config.minimumRequirementEnabled;
            }
            if (config.minimumRequiredEvents !== undefined) {
                updates.minimumRequiredEvents = config.minimumRequiredEvents as number;
            }
            if (config.minimumPenaltyPoints !== undefined) {
                updates.minimumPenaltyPoints = config.minimumPenaltyPoints as number;
            }
            if (config.audience !== undefined) {
                updates.audience = config.audience as string;
            }
            if (config.departmentIds !== undefined) {
                updates.departmentIds = config.departmentIds as number[];
            }
            return { ...prev, ...updates };
        });
    };

    const handleRuleToggle = (ruleKey: string, enabled: boolean) => {
        setEnabledRules(prev => ({
            ...prev,
            [ruleKey]: enabled
        }));
    };

    const handlePreview = async () => {
        if (!selectedPresetCode) return;
        setPreviewLoading(true);
        try {
            const res = await seriesAPI.previewSeriesPreset({
                presetCode: selectedPresetCode as SeriesPresetCode,
                presetConfig: formData.presetConfig
            });
            if (res.status && res.data) {
                setPresetPreview(res.data);
                setFormData(prev => ({
                    ...prev,
                    scoreType: res.data!.scoreType,
                    milestonePoints: res.data!.milestonePoints,
                    minimumRequirementEnabled: res.data!.minimumRequirementEnabled ?? false,
                    minimumRequiredEvents: res.data!.minimumRequiredEvents ?? undefined,
                    minimumPenaltyPoints: res.data!.minimumPenaltyPoints !== undefined && res.data!.minimumPenaltyPoints !== null ? Number(res.data!.minimumPenaltyPoints) : undefined
                }));
            }
        } catch (error) {
            console.error('Lỗi khi xem trước mẫu cấu hình:', error);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSemesterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            targetSemesterId: value === '' ? null : parseInt(value)
        }));
    };

    const handleUnlimitedTicketsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUnlimitedTickets(checked);
        if (checked) {
            setFormData(prev => ({ ...prev, ticketQuantity: undefined }));
        } else {
            setFormData(prev => ({ ...prev, ticketQuantity: 0 }));
        }
    };

    const addMilestone = () => {
        const count = parseInt(milestoneInput.count);
        const points = parseFloat(milestoneInput.points);

        if (!count || count < 1 || !points || points < 0) {
            toast.error('Vui lòng nhập số sự kiện và điểm hợp lệ');
            return;
        }

        if (milestoneEntries.some(e => e.count === count)) {
            toast.error('Mốc này đã tồn tại');
            return;
        }

        const newEntries = [...milestoneEntries, { count, points }].sort((a, b) => a.count - b.count);
        setMilestoneEntries(newEntries);

        const milestoneObj: Record<number, number> = {};
        newEntries.forEach(e => {
            milestoneObj[e.count] = e.points;
        });

        setFormData(prev => ({
            ...prev,
            milestonePoints: milestoneObj
        }));

        setMilestoneInput({ count: '', points: '' });
    };

    const removeMilestone = (count: number) => {
        const newEntries = milestoneEntries.filter(e => e.count !== count);
        setMilestoneEntries(newEntries);

        const milestoneObj: Record<number, number> = {};
        newEntries.forEach(e => {
            milestoneObj[e.count] = e.points;
        });

        setFormData(prev => ({
            ...prev,
            milestonePoints: milestoneObj
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Tên chuỗi sự kiện là bắt buộc';
        }

        const isPresetMode = !!selectedPresetCode;

        if (isPresetMode) {
            // Validate preset config fields using supportedRules metadata
            const selectedPreset = presets.find(p => p.code === selectedPresetCode);
            if (selectedPreset) {
                const result = validateSeriesPresetConfig(
                    selectedPreset,
                    enabledRules,
                    (formData.presetConfig as Record<string, unknown>) || {}
                );
                for (const err of result.errors) {
                    newErrors[err.fieldName] = err.message;
                }
            }
        } else {
            // CUSTOM mode validation (milestone only — minimum requirements are in presetConfig)
            if (!formData.milestonePoints || Object.keys(formData.milestonePoints).length === 0) {
                newErrors.milestonePoints = 'Vui lòng thêm ít nhất một mốc điểm';
            }
        }

        if (formData.registrationStartDate && formData.registrationDeadline) {
            if (new Date(formData.registrationStartDate) >= new Date(formData.registrationDeadline)) {
                newErrors.registrationDeadline = 'Hạn đăng ký phải sau ngày mở đăng ký';
            }
        }

        // Khoa tổ chức bắt buộc
        if (!formData.organizerIds || formData.organizerIds.length === 0) {
            newErrors.organizerIds = 'Chọn ít nhất một khoa tổ chức';
        } else if (userRole === Role.MANAGER) {
            const scopeError = assertManagerOrganizers(
                formData.organizerIds,
                managerDepartmentIds
            );
            if (scopeError) {
                newErrors.organizerIds = scopeError;
            }
        }

        // Cross-field validation: departmentIds required when audience scoped
        if (formData.audience && formData.audience !== 'ALL_PARTICIPANTS') {
            if (!formData.departmentIds || formData.departmentIds.length === 0) {
                newErrors.departmentIds = 'Vui lòng chọn ít nhất một khoa khi giới hạn đối tượng';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        const isCustomMode = !selectedPresetCode;

        if (!isCustomMode) {
            // Preset mode: BE applySeriesPreset fills milestone + minimum fields from presetConfig
            const payload = {
                ...formData,
                presetCode: selectedPresetCode as SeriesPresetCode,
                presetConfig: formData.presetConfig,
                milestonePoints: undefined,
                minimumRequirementEnabled: undefined,
                minimumRequiredEvents: undefined,
                minimumPenaltyPoints: undefined
            } as unknown as CreateSeriesRequest;
            onSubmit(payload);
        } else {
            // CUSTOM mode: presetConfig carries minimum requirement fields (rendered by PresetConfigPanel)
            const payload: CreateSeriesRequest = {
                ...formData,
                presetCode: 'CUSTOM' as SeriesPresetCode,
                presetConfig: formData.presetConfig,
                minimumRequirementEnabled: undefined,
                minimumRequiredEvents: undefined,
                minimumPenaltyPoints: undefined
            };
            onSubmit(payload);
        }
    };

    const getScoreTypeLabel = (type: ScoreType) => {
        const labels: Record<ScoreType, string> = {
            [ScoreType.REN_LUYEN]: 'Rèn luyện',
            [ScoreType.CONG_TAC_XA_HOI]: 'Công tác xã hội',
            [ScoreType.CHUYEN_DE]: 'Chuyên đề'
        };
        return labels[type] || type;
    };

    const isCustomMode = !selectedPresetCode;

    const inputClass = (hasError?: boolean) =>
        `w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-900/25 focus:border-primary-900/40 ${
            hasError ? 'border-rose-400' : 'border-gray-200 hover:border-gray-300'
        }`;

    const sectionCard = 'rounded-2xl border border-gray-100 bg-white shadow-premium p-5 sm:p-6 space-y-5';

    const presetPanel = presets.length > 0 ? (
        <PresetConfigPanel
            presets={presets as any}
            selectedPresetCode={selectedPresetCode}
            onPresetChange={handlePresetCodeChange}
            config={(formData.presetConfig as Record<string, unknown>) || {}}
            onConfigChange={handlePresetConfigChange}
            enabledRules={enabledRules}
            onRuleToggle={handleRuleToggle}
            onPreview={handlePreview}
            previewResponse={presetPreview}
            previewLoading={previewLoading}
            mode="series"
            errors={errors}
            externalOptions={externalOptions}
        />
    ) : (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center">
            <p className="text-sm font-medium text-gray-600">Đang tải mẫu cấu hình…</p>
        </div>
    );

    return (
        <div className={embedded ? '' : 'max-w-6xl mx-auto p-6'}>
            <div className={embedded ? '' : 'bg-white shadow-lg rounded-lg overflow-hidden'}>
                {!embedded && (
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                        <p className="text-gray-600 mt-1">Điền thông tin chi tiết về chuỗi sự kiện</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                        {/* Left column — series info */}
                        <div className="xl:col-span-7 space-y-6 min-w-0">
                            <section className={sectionCard}>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                                        Thông tin chuỗi
                                    </p>
                                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-primary-900">
                                        Chi tiết cơ bản
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                                        Tên, mô tả và loại điểm áp dụng cho các mốc thưởng.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Tên chuỗi sự kiện <span className="text-rose-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={inputClass(!!errors.name)}
                                            placeholder="Nhập tên chuỗi sự kiện"
                                        />
                                        {errors.name && <p className="text-rose-600 text-sm mt-1">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Mô tả
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            value={formData.description || ''}
                                            onChange={handleChange}
                                            rows={4}
                                            className={inputClass()}
                                            placeholder="Nhập mô tả chuỗi sự kiện"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="scoreType" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Loại điểm <span className="text-rose-500">*</span>
                                            </label>
                                            <select
                                                id="scoreType"
                                                name="scoreType"
                                                value={formData.scoreType}
                                                onChange={handleChange}
                                                disabled={!isCustomMode && !!selectedPresetCode}
                                                className={`${inputClass()} disabled:bg-gray-50 disabled:text-gray-500`}
                                            >
                                                <option value={ScoreType.REN_LUYEN}>Rèn luyện</option>
                                                <option value={ScoreType.CONG_TAC_XA_HOI}>Công tác xã hội</option>
                                                <option value={ScoreType.CHUYEN_DE}>Chuyên đề</option>
                                            </select>
                                            {!isCustomMode && selectedPresetCode && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Đồng bộ từ mẫu cấu hình — dùng Preview để xem trước.
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="targetSemesterId" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Học kỳ cộng điểm milestone
                                            </label>
                                            <select
                                                id="targetSemesterId"
                                                name="targetSemesterId"
                                                value={formData.targetSemesterId ?? ''}
                                                onChange={handleSemesterChange}
                                                className={inputClass()}
                                            >
                                                <option value="">Tự động (theo thời gian sự kiện đầu tiên)</option>
                                                {semesters.map(semester => (
                                                    <option key={semester.id} value={semester.id}>
                                                        {semester.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Bỏ trống để hệ thống tự suy luận học kỳ.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-1">
                                        <OrganizerSelector
                                            selectedIds={formData.organizerIds || []}
                                            onChange={(ids) => {
                                                setFormData(prev => ({ ...prev, organizerIds: ids }));
                                                if (errors.organizerIds) {
                                                    setErrors(prev => ({ ...prev, organizerIds: '' }));
                                                }
                                            }}
                                            error={errors.organizerIds}
                                            required
                                            label="Khoa tổ chức"
                                        />
                                        <p className="text-xs text-gray-500 mt-2">
                                            Sự kiện con sẽ dùng khoa này. Không chỉnh khoa tổ chức ở từng sự kiện con.
                                        </p>
                                    </div>

                                    <label className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50">
                                        <input
                                            type="checkbox"
                                            checked={formData.requiresApproval}
                                            onChange={(e) =>
                                                setFormData(prev => ({ ...prev, requiresApproval: e.target.checked }))
                                            }
                                            className="mt-0.5 rounded border-gray-300 text-primary-900 focus:ring-primary-900/30"
                                        />
                                        <span className="text-sm text-gray-700">Yêu cầu duyệt đăng ký trước khi tham gia</span>
                                    </label>
                                </div>
                            </section>

                            {isCustomMode && (
                                <section className={sectionCard}>
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                                            Mốc điểm
                                        </p>
                                        <h3 className="mt-1 text-lg font-semibold tracking-tight text-primary-900">
                                            Điểm milestone
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Định nghĩa các mốc điểm thưởng khi hoàn thành số lượng sự kiện nhất định.
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                value={milestoneInput.count}
                                                onChange={(e) => setMilestoneInput(prev => ({ ...prev, count: e.target.value }))}
                                                placeholder="Số sự kiện"
                                                className={`${inputClass()} sm:flex-1 tabular-nums`}
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                value={milestoneInput.points}
                                                onChange={(e) => setMilestoneInput(prev => ({ ...prev, points: e.target.value }))}
                                                placeholder="Điểm thưởng"
                                                className={`${inputClass()} sm:flex-1 tabular-nums`}
                                            />
                                            <button
                                                type="button"
                                                onClick={addMilestone}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-900 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-800 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-900/30"
                                            >
                                                <Plus size={18} weight="bold" />
                                                Thêm mốc
                                            </button>
                                        </div>

                                        {errors.milestonePoints && (
                                            <p className="text-rose-600 text-sm">{errors.milestonePoints}</p>
                                        )}

                                        {milestoneEntries.length > 0 ? (
                                            <ul className="space-y-2">
                                                {milestoneEntries.map((entry) => (
                                                    <li
                                                        key={entry.count}
                                                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3"
                                                    >
                                                        <span className="text-sm font-medium text-gray-900 tabular-nums">
                                                            {entry.count} sự kiện → {entry.points} điểm{' '}
                                                            {getScoreTypeLabel(formData.scoreType)}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeMilestone(entry.count)}
                                                            className="inline-flex items-center gap-1 text-sm font-medium text-rose-600 hover:text-rose-800 transition-colors focus:outline-none focus:ring-2 focus:ring-rose-200 rounded-lg px-2 py-1"
                                                        >
                                                            <Trash size={16} />
                                                            Xóa
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center">
                                                Chưa có mốc điểm — thêm ít nhất một mốc để tiếp tục.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            )}

                            <section className={sectionCard}>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                                        Đăng ký
                                    </p>
                                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-primary-900">
                                        Thời gian & đối tượng
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="registrationStartDate" className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Ngày mở đăng ký
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="registrationStartDate"
                                            name="registrationStartDate"
                                            value={formData.registrationStartDate || ''}
                                            onChange={handleChange}
                                            className={inputClass()}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Hạn đăng ký
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="registrationDeadline"
                                            name="registrationDeadline"
                                            value={formData.registrationDeadline || ''}
                                            onChange={handleChange}
                                            className={inputClass(!!errors.registrationDeadline)}
                                        />
                                        {errors.registrationDeadline && (
                                            <p className="text-rose-600 text-sm mt-1">{errors.registrationDeadline}</p>
                                        )}
                                    </div>
                                </div>

                                {isCustomMode && (
                                    <div className="space-y-4 pt-2 border-t border-gray-100">
                                        <div>
                                            <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Khoa áp dụng điểm (audience)
                                            </label>
                                            <select
                                                id="audience"
                                                name="audience"
                                                value={formData.audience || 'ALL_PARTICIPANTS'}
                                                onChange={handleChange}
                                                className={inputClass()}
                                            >
                                                <option value="ALL_PARTICIPANTS">Tất cả người tham gia</option>
                                                <option value="DEPARTMENT_ONLY">Chỉ sinh viên thuộc khoa được chọn</option>
                                                <option value="OUTSIDE_DEPARTMENTS_ONLY">Chỉ sinh viên ngoài khoa được chọn</option>
                                            </select>
                                        </div>
                                        {formData.audience && formData.audience !== 'ALL_PARTICIPANTS' && (
                                            <MultiSelectField
                                                label="Danh sách khoa áp dụng điểm"
                                                options={departments.map(d => ({ value: d.id, label: d.name }))}
                                                value={formData.departmentIds || []}
                                                onChange={(val) => setFormData(prev => ({ ...prev, departmentIds: val as number[] }))}
                                                required={formData.audience !== 'ALL_PARTICIPANTS'}
                                                error={errors.departmentIds}
                                            />
                                        )}
                                    </div>
                                )}
                            </section>

                            <section className={sectionCard}>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                                        Vé & trạng thái
                                    </p>
                                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-primary-900">
                                        Số lượng và công bố
                                    </h3>
                                </div>

                                <div className="space-y-4">
                                    <label className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50">
                                        <input
                                            type="checkbox"
                                            checked={unlimitedTickets}
                                            onChange={handleUnlimitedTicketsChange}
                                            className="mt-0.5 rounded border-gray-300 text-primary-900 focus:ring-primary-900/30"
                                        />
                                        <span className="text-sm text-gray-700">Không giới hạn số lượng vé</span>
                                    </label>

                                    {!unlimitedTickets && (
                                        <div>
                                            <label htmlFor="ticketQuantity" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Số lượng vé
                                            </label>
                                            <input
                                                type="number"
                                                id="ticketQuantity"
                                                name="ticketQuantity"
                                                min="1"
                                                value={formData.ticketQuantity || 0}
                                                onChange={handleChange}
                                                className={`${inputClass()} tabular-nums max-w-xs`}
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2 pt-2 border-t border-gray-100">
                                        <label className="flex items-start gap-3 rounded-xl px-1 py-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="isImportant"
                                                checked={!!formData.isImportant}
                                                onChange={handleChange}
                                                className="mt-0.5 rounded border-gray-300 text-primary-900 focus:ring-primary-900/30"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Sự kiện quan trọng (tự động đăng ký tất cả sinh viên active)
                                            </span>
                                        </label>
                                        <label className="flex items-start gap-3 rounded-xl px-1 py-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="mandatoryForFacultyStudents"
                                                checked={!!formData.mandatoryForFacultyStudents}
                                                onChange={handleChange}
                                                className="mt-0.5 rounded border-gray-300 text-primary-900 focus:ring-primary-900/30"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Bắt buộc với sinh viên khoa tổ chức (auto đăng ký)
                                            </span>
                                        </label>
                                        <label className="flex items-start gap-3 rounded-xl px-1 py-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="isDraft"
                                                checked={!!formData.isDraft}
                                                onChange={handleChange}
                                                className="mt-0.5 rounded border-gray-300 text-primary-900 focus:ring-primary-900/30"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Bản nháp (chưa công bố, không tự động đăng ký)
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Right column — preset / score config */}
                        <aside className="xl:col-span-5 min-w-0">
                            <div className="xl:sticky xl:top-24 rounded-2xl border border-primary-900/10 bg-white shadow-premium overflow-hidden">
                                <div className="border-b border-gray-100 bg-primary-900 px-5 py-4 text-white">
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent/90">
                                        Điểm & mẫu cấu hình
                                    </p>
                                    <h3 className="mt-1 text-lg font-semibold tracking-tight">
                                        Cấu hình mốc điểm
                                    </h3>
                                    <p className="mt-1 text-sm text-white/65 leading-relaxed">
                                        Chọn mẫu chuỗi sự kiện hoặc tùy chỉnh mốc điểm thủ công.
                                    </p>
                                </div>
                                <div className="p-5 space-y-5 max-h-[min(70vh,720px)] overflow-y-auto">
                                    {presetPanel}
                                </div>
                            </div>
                        </aside>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                className="rounded-xl border border-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-gray-200"
                            >
                                Hủy
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="rounded-xl bg-primary-900 px-6 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-900/30"
                        >
                            {loading ? 'Đang xử lý…' : 'Lưu chuỗi sự kiện'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SeriesForm;
